/**
 * Monthly Reports — Learn-Mode inline tips.
 *
 * Covers `routes/social/_sections/analytics/ReportTab.tsx` and the
 * `components/reports/*` pieces it renders, including the dynamic
 * `data-help-context={helpContext}` sections of `ReportPreview.tsx`'s
 * `<Section>` wrapper (`report-summary`, `report-follower-growth`,
 * `report-top-posts`, `report-best-time`, `report-formats`,
 * `report-next-month`, `report-caveats`, `report-benchmarks`,
 * `report-web-impact`).
 */

import type { LearnTip } from "./types";

export const reportsTips: LearnTip[] = [
  {
    key: "report-toolbar",
    title: "Building a Report",
    body: "Pick the client and month to generate a monthly report for, then download it as a PDF or share a link with the client.",
  },
  {
    key: "report-download-pdf",
    title: "Download PDF",
    body: "Builds this report as a PDF you can save or send yourself, outside of the no-login share link.",
  },
  {
    key: "report-preview",
    title: "Monthly Report",
    body: "A preview of exactly what the client sees — this is the same report that opens from a shared link or downloads as a PDF.",
  },
  {
    key: "report-narrative",
    title: "Report Summary",
    body: "An AI-drafted executive summary — what worked and what to try next month — written from this report's own numbers. Review and edit it before sending; once your team edits it, your version is what appears in the PDF.",
  },
  {
    key: "report-summary",
    title: "Summary",
    body: "The headline numbers for the month: followers, reach, and engagement, compared against the previous period.",
  },
  {
    key: "report-views-kpi",
    title: "Views",
    body: "How many times this month's posts were viewed — Meta's headline metric, which replaced impressions. Counted from the posts published this month and compared with the posts from the period before. Platforms that don't share view counts show \"Not reported\" instead of a zero.",
  },
  {
    key: "report-stories-row",
    title: "Instagram Stories",
    body: 'How many Instagram Stories went up this month and how many views they earned, compared with the period before when there is one to compare. Stories are reported separately and never counted as posts. When none went up, the report says "No stories this month".',
  },
  {
    key: "report-by-platform",
    title: "By Platform",
    body: "This month's results for each connected account. Instagram accounts also list the Stories they posted and the views those stories earned. Instagram only shares a story's numbers while it is live (24 hours), so these are the last numbers captured before it expired.",
  },
  {
    key: "report-follower-growth",
    title: "Follower Growth",
    body: "How this client's follower count changed over the reporting period, so you can see the trend at a glance.",
  },
  {
    key: "report-top-posts",
    title: "Top Posts",
    body: "The best-performing posts from the period, ranked by engagement — useful for pointing out what resonated with the client's audience.",
  },
  {
    key: "report-best-time",
    title: "Best Time to Post",
    body: "When this client's audience engages most, based on their own post history — a data-backed suggestion for scheduling future posts.",
  },
  {
    key: "report-formats",
    title: "What Formats Performed",
    body: "Compares how different post formats (photo, carousel, video/Reel) performed for this client over the period.",
  },
  {
    key: "report-next-month",
    title: "What to Try Next Month",
    body: "AI-suggested next steps based on this month's results — a starting point for the strategy conversation with the client.",
  },
  {
    key: "report-caveats",
    title: "About This Data",
    body: "Notes on where the numbers come from and any limits on what's measured, so the client understands what the report does and doesn't cover.",
  },
  {
    key: "report-share-links",
    title: "Sharing This Report",
    body: "Creates a read-only link that opens this month's report as a PDF. No login is needed, so only share it directly with the client.",
  },
  {
    key: "report-white-label-settings",
    title: "White-Label Settings",
    body: "Reports always use each client's own logo and brand colors. These settings add your agency's name, logo, and footer on top — nothing else changes. Apply them to one client or set them as the agency default.",
  },
  {
    key: "report-white-label-footer",
    title: "Report Footer",
    body: "Shows your agency's branding and who prepared the report, based on your white-label settings.",
  },
  // ── Optional sections + scheduled delivery (lane D2, 2026-10-04) ──
  {
    key: "report-link-in-bio",
    title: "Link in Bio",
    body: "Visits to this client's link-in-bio page and the links people tapped, with click-through rate (clicks divided by page views) and where visitors came from. Only shows when the page had visits; there is no comparison until the page has been live for a full prior period.",
  },
  {
    key: "report-competitors",
    title: "You vs. Competitors",
    body: 'The client next to the competitors you track on the Competitors tab: followers and growth this month, posts per week and public engagement rate, ranked by engagement rate. Numbers are public and measured the same way for everyone. "Tracking since" shows when each competitor was first captured.',
  },
  {
    key: "report-mentions",
    title: "Mentions and Tagged Posts",
    body: "How often people mentioned or tagged this client (from the Listening page), by source, with a rough sentiment split and links to the most-engaged posts. The report never includes what people wrote or who wrote it, only counts and links.",
  },
  // ── Benchmarks + website impact (lanes F2/F3, 2026-10-06) ──
  {
    key: "report-benchmarks",
    title: "Benchmarks",
    body: "This client's engagement rate, posting pace and follower growth against other businesses we work with in the same industry, computed once a month. Only shown once at least 5 similar businesses report data, and never names another business.",
  },
  {
    key: "report-web-impact",
    title: "Website Impact",
    body: "Website sessions and conversions Google Analytics attributes to this client's social posts, by network, from the client's own GA4 property. Only shown once GA4 is linked and the month had at least one social session.",
  },
  {
    key: "report-schedule-button",
    title: "Schedule Delivery",
    body: "Email this client their report automatically every month. Nothing is sent until you add recipients, turn the schedule on and confirm.",
  },
  {
    key: "report-schedule-delivery",
    title: "Monthly Report Email",
    body: "Choose who receives the report, the day and time it goes out (in the client's timezone) and whether the PDF is attached. Each month's email covers the month before, includes a read-only link that works for 30 days, and is sent once per month.",
  },
  {
    key: "report-schedule-send-test",
    title: "Send Me a Test",
    body: "Emails this report only to you, so you can check exactly what the client will get. It never goes to the client's recipients.",
  },
  {
    key: "report-schedule-deliveries",
    title: "Recent Report Emails",
    body: 'Every scheduled send, test and failure for this client. "Would have sent" means scheduled emails are switched off on the server for now, so nothing went out.',
  },
];
